import { useEffect, useId, useRef, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import clsx from "clsx";
import { useTranslation } from "react-i18next";

/**
 * Accessible modal on the native <dialog> (focus trap and Esc for free). It opens with the
 * field marked data-autofocus focused, else the dialog itself, so nothing shows a ring and
 * the first Tab reaches the first field (the close button comes last in the tab order). On a
 * phone it rises from the bottom as a sheet with full-width buttons.
 */
export function Modal(props: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  /** One line under the title: what the form is for. */
  description?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  size?: "sm" | "md" | "lg" | "xl";
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const { t } = useTranslation();
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (props.open && !d.open) {
      // The browser focuses what carries autofocus as the dialog opens; without it, the first
      // focusable thing, which is the heading (a picker first would pop its list open).
      d.querySelector<HTMLElement>("[data-autofocus]")?.setAttribute("autofocus", "");
      d.showModal();
    }
    if (!props.open && d.open) d.close();
  }, [props.open]);
  if (!props.open) return null;
  return createPortal(
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        props.onClose();
      }}
      className={clsx(
        // No `relative` here: it would undo the open dialog's own position: fixed (the top layer
        // then places it from the page's top, off screen once the page is scrolled).
        "modal m-auto w-[calc(100%-2rem)] overflow-hidden rounded-[20px] border-0 bg-surface p-0 text-ink shadow-2xl outline-none backdrop:bg-ink/35",
        "max-sm:mb-0 max-sm:w-full max-sm:max-w-none max-sm:rounded-b-none max-sm:rounded-t-[22px]",
        { sm: "max-w-md", md: "max-w-xl", lg: "max-w-3xl", xl: "max-w-5xl" }[props.size ?? "md"],
      )}
    >
      <div className="mx-auto mt-2.5 h-1 w-10 rounded-full bg-line sm:hidden" aria-hidden />
      <div tabIndex={-1} className="px-6 pt-5 pe-16 pb-1 outline-none max-sm:pt-3">
        <h2 id={titleId} className="text-[18px] leading-snug font-bold">{props.title}</h2>
        {props.description && <p className="mt-1 text-[13.5px] leading-relaxed text-ink-3">{props.description}</p>}
      </div>
      <div className="max-h-[70vh] overflow-y-auto px-6 pt-4 pb-5 max-sm:max-h-[72dvh]">{props.children}</div>
      {props.footer && (
        <div className="flex flex-wrap items-center justify-end gap-2 border-t border-line-2 px-6 py-4 max-sm:pb-[calc(1rem+env(safe-area-inset-bottom))] max-sm:[&>*]:flex-1">
          {props.footer}
        </div>
      )}
      <button type="button" className="btn-ghost absolute end-3.5 top-3.5 p-2 text-ink-3 max-sm:top-4" onClick={props.onClose} aria-label={t("common.close")}>
        <X className="size-[18px]" />
      </button>
    </dialog>,
    document.body,
  );
}

/** Side panel that slides in from the inline end (left in RTL); on a phone it takes the whole screen. */
export function Drawer(props: { open: boolean; onClose: () => void; title: ReactNode; children: ReactNode; actions?: ReactNode }) {
  const { t } = useTranslation();
  const titleId = useId();
  useEffect(() => {
    if (!props.open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && props.onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [props.open, props.onClose]);
  if (!props.open) return null;
  return createPortal(
    <div className="fixed inset-0 z-40 flex justify-end" role="dialog" aria-modal="true" aria-labelledby={titleId}>
      <button type="button" aria-label={t("common.close")} className="fade-in absolute inset-0 bg-ink/30" onClick={props.onClose} />
      <aside className="drawer-panel relative flex h-full w-full max-w-2xl flex-col bg-surface shadow-2xl">
        <header className="flex items-center gap-2 border-b border-line-2 px-6 py-4">
          <h2 id={titleId} className="flex-1 truncate text-[17px] font-bold">{props.title}</h2>
          {props.actions}
          <button type="button" className="btn-ghost p-2 text-ink-3" onClick={props.onClose} aria-label={t("common.close")}>
            <X className="size-[18px]" />
          </button>
        </header>
        <div className="flex-1 overflow-y-auto">{props.children}</div>
      </aside>
    </div>,
    document.body,
  );
}

export function ConfirmDialog(props: {
  open: boolean;
  title: ReactNode;
  message: ReactNode;
  confirmLabel: ReactNode;
  danger?: boolean;
  busy?: boolean;
  disabled?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <Modal
      open={props.open}
      onClose={props.onClose}
      title={props.title}
      size="sm"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={props.onClose}>{t("common.cancel")}</button>
          <button type="button" className={props.danger ? "btn-danger" : "btn-primary"} disabled={props.busy || props.disabled} onClick={props.onConfirm}>
            {props.confirmLabel}
          </button>
        </>
      }
    >
      <p className="leading-relaxed text-ink-2">{props.message}</p>
      {props.children}
    </Modal>
  );
}
